import { useId } from 'react';
import PropTypes from 'prop-types';
import {
  getUsageColor,
  getUsagePercentage,
  getRemaining,
  USAGE_COLORS
} from '../../utils/constants/subscription';

const getValueText = ({
  isConfigured,
  isPercentageMode,
  percentRemaining,
  formattedUsed,
  formattedLimit,
  unitSuffix
}) => {
  if (!isConfigured) return 'Not configured';
  if (isPercentageMode) return `${percentRemaining}% remaining`;
  return `${formattedUsed} of ${formattedLimit}${unitSuffix} used`;
};

const UsageProgressBar = ({
  label,
  used = 0,
  limit = 0,
  unit,
  showRemaining = false,
  displayMode = 'absolute',
  forceGrey = false
}) => {
  const labelId = useId();
  const percentage = getUsagePercentage(used, limit);
  // TD-2119 R3: forceGrey wins over the threshold-based colour so the on-hold
  // sidebar can present a grey bar regardless of usage percentage.
  const color = forceGrey ? 'grey' : getUsageColor(used, limit);
  const remaining = getRemaining(used, limit);
  const formattedUsed = used.toLocaleString();
  const formattedLimit = limit.toLocaleString();
  const formattedRemaining = remaining.toLocaleString();
  const isConfigured = limit > 0;
  const percentRemaining = Math.max(0, 100 - percentage);

  // 'bare' renders the track alone — the caller shows the figure itself, which
  // is how the agent sidebar keeps a meter to two lines instead of three.
  const isBare = displayMode === 'bare';
  const isPercentageMode = displayMode === 'percentage' || isBare;
  const unitSuffix = unit ? ` ${unit}` : '';
  const valueText = getValueText({
    isConfigured,
    isPercentageMode,
    percentRemaining,
    formattedUsed,
    formattedLimit,
    unitSuffix
  });
  // Fill the bar with the *remaining* portion in percentage mode so visual
  // length matches the "X% remaining" label per Confluence R1 mockup.
  const fillWidth = isPercentageMode ? percentRemaining : percentage;
  const ariaValueNow = isPercentageMode ? percentRemaining : percentage;

  if (!isConfigured) {
    return (
      <div className="usage-progress-bar usage-progress-not-configured">
        {label && (
          <div className="usage-progress-header">
            <span className="usage-progress-label" id={labelId}>
              {label}
            </span>
            <span className="usage-progress-count">Not configured</span>
          </div>
        )}
        {/*
          TD-2120 Item 2 — deliberate, team-agreed decision (do NOT migrate to
          a native <progress>). role="progressbar" with aria-valuenow/min/max +
          aria-valuetext is already WCAG-correct, so swapping to <progress>
          buys zero accessibility. <progress> also can't render this bar's
          custom states — the green/yellow/red/grey colour thresholds, the
          inverse "remaining" fill in percentage mode, and the warning/critical
          badges — and is notoriously inconsistent to style cross-browser. The
          Sonar "use <progress>" smell is suppressed by this design choice.
        */}
        <div
          className="usage-progress-track"
          role="progressbar"
          aria-valuenow={0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-labelledby={label ? labelId : undefined}
          aria-valuetext="Not configured"
        >
          <div className="usage-progress-fill" style={{ width: '0%' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="usage-progress-bar">
      {(label || !isPercentageMode) && (
        <div className="usage-progress-header">
          {label && (
            <span className="usage-progress-label" id={labelId}>
              {label}
              {color === USAGE_COLORS.WARNING && (
                <span className="usage-status-badge usage-status-warning"> Warning</span>
              )}
              {color === USAGE_COLORS.CRITICAL && (
                <span className="usage-status-badge usage-status-critical"> Critical</span>
              )}
            </span>
          )}
          {!isPercentageMode && (
            <span className="usage-progress-count">
              {formattedUsed} / {formattedLimit}
              {unitSuffix}
            </span>
          )}
        </div>
      )}
      {/* role="progressbar" retained by design — see the not-configured branch above. */}
      <div
        className="usage-progress-track"
        role="progressbar"
        aria-valuenow={ariaValueNow}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-labelledby={label ? labelId : undefined}
        aria-valuetext={valueText}
      >
        <div className={`usage-progress-fill usage-${color}`} style={{ width: `${fillWidth}%` }} />
      </div>
      {isPercentageMode && !isBare && (
        <div className="usage-progress-percent-remaining">{percentRemaining}% remaining</div>
      )}
      {!isPercentageMode && showRemaining && (
        <div className="usage-progress-remaining">{formattedRemaining} remaining</div>
      )}
    </div>
  );
};

UsageProgressBar.propTypes = {
  label: PropTypes.string,
  used: PropTypes.number,
  limit: PropTypes.number,
  unit: PropTypes.string,
  showRemaining: PropTypes.bool,
  displayMode: PropTypes.oneOf(['absolute', 'percentage', 'bare']),
  forceGrey: PropTypes.bool
};

export default UsageProgressBar;
